// Breakable supply crate: a prop registered with the enemy manager so projectiles
// and explosions can hit it (kind 'prop' never counts for room clear or targeting).
// It drops its own scrap (scrapValue is 0 so the generic kill drop never doubles it).

import Phaser from 'phaser';
import { DEPTH } from '../../config/constants';
import { COLORS } from '../../config/palette';
import type { EnemyApi, GameContext, HitArea } from '../../core/contracts';
import type { DamageInfo, DamageResult, OrbType, StatusId, StatusState } from '../../core/types';
import { crateTex } from './textures';

const CRATE_HP = 30;
const SIZE = 56;
/** Chance a crate holds an orb instead of only scrap. */
const ORB_CHANCE = 0.07;
let nextUid = 0x7c000;

export class Crate implements EnemyApi {
  readonly uid = nextUid++;
  readonly defId = 'crate' as const;
  readonly kind = 'prop' as const;
  readonly name = 'SUPPLY CRATE';
  readonly elite = [] as const;
  readonly obj: Phaser.GameObjects.Image;
  readonly body: Phaser.Physics.Arcade.Body;
  readonly radius = SIZE / 2;
  hp = CRATE_HP;
  readonly maxHp = CRATE_HP;
  armorHp = 0;
  alive = true;
  readonly spawning = false;
  readonly flying = false;
  readonly statuses = new Map<StatusId, StatusState>();
  readonly knockbackResist = 1;
  readonly scrapValue = 0;
  private flashTimer: Phaser.Time.TimerEvent | null = null;
  private areas: HitArea[] = [{ x: 0, y: 0, w: SIZE, h: SIZE, mult: 1 }];

  constructor(
    private ctx: GameContext,
    x: number,
    feetY: number,
  ) {
    const scene = ctx.scene;
    this.obj = scene.add.image(x, feetY - SIZE / 2, crateTex(scene)).setDepth(DEPTH.PROPS);
    this.obj.setDisplaySize(SIZE + 8, SIZE + 8);
    scene.physics.add.existing(this.obj, false);
    this.body = this.obj.body as Phaser.Physics.Arcade.Body;
    this.body.setSize(this.obj.width * (SIZE / (SIZE + 8)), this.obj.height * (SIZE / (SIZE + 8)), true);
    this.body.setImmovable(true);
    this.body.setAllowGravity(false);
    this.body.moves = false;
  }

  get x(): number {
    return this.obj.x;
  }

  get y(): number {
    return this.obj.y;
  }

  hitAreas(): readonly HitArea[] {
    const a = this.areas[0];
    a.x = this.body.x;
    a.y = this.body.y;
    a.w = this.body.width;
    a.h = this.body.height;
    return this.areas;
  }

  receiveDamage(amount: number, info: DamageInfo): DamageResult {
    if (!this.alive) return { dealt: 0, killed: false, blocked: false, absorbed: 0 };
    const dealt = Math.min(this.hp, Math.max(0, amount));
    this.hp -= dealt;
    if (this.hp <= 0) {
      this.shatter(info);
      return { dealt, killed: true, blocked: false, absorbed: 0 };
    }
    // Hit flash + a small jolt away from the shot.
    this.obj.setTintFill(0xffffff);
    this.flashTimer?.remove();
    this.flashTimer = this.ctx.scene.time.delayedCall(60, () => this.obj.active && this.obj.clearTint());
    const dir = info.sourceX !== undefined ? Math.sign(this.obj.x - info.sourceX) || 1 : 1;
    this.obj.setAngle(dir * 6);
    this.ctx.scene.tweens.add({ targets: this.obj, angle: 0, duration: 160, ease: 'Back.easeOut' });
    return { dealt, killed: false, blocked: false, absorbed: 0 };
  }

  private shatter(info: DamageInfo): void {
    this.alive = false;
    const { ctx } = this;
    const x = this.obj.x;
    const y = this.obj.y;
    try {
      ctx.fx.debris(x, y, { count: 14, color: COLORS.gunmetal, speed: 420 });
      ctx.fx.sparks(x, y, -Math.PI / 2, { count: 8, color: COLORS.scrap, spread: Math.PI });
      ctx.fx.smoke(x, y, { count: 3, color: COLORS.steel2 });
    } catch (err) {
      console.warn('[world] crate fx failed', err);
    }
    ctx.audio.play('explosion_small', { volume: 0.5, pitch: 1.3 });
    ctx.loot.spawnScrap(x, y - 10, 5 + ctx.rng.int(0, 2) * 3 + ctx.run.sectorIndex * 2);
    if (ctx.rng.chance(ORB_CHANCE)) {
      const type = ctx.rng.weighted<OrbType>(['arsenal', 'armor', 'unstable'], (t) => (t === 'unstable' ? 1 : 2))!;
      ctx.loot.spawnOrb(type, x, y - 40, { source: 'crate', dropIn: false });
    }
    void info;
    this.destroyObject();
    try {
      ctx.enemies.unregister(this);
    } catch {
      // The manager may already drop dead entries itself.
    }
  }

  knockback(_vx: number, _vy: number): void {
    // Crates are bolted down.
  }

  pull(_ax: number, _ay: number, _dt: number): void {
    // Unaffected by gravity fields.
  }

  /** Remove the game object (idempotent; also used by the world on room teardown). */
  destroyObject(): void {
    this.flashTimer?.remove();
    this.flashTimer = null;
    if (this.obj.active) this.obj.destroy();
  }
}
